import { useState, useEffect, useRef } from 'react'
import ReactMarkdown from 'react-markdown'
import classes from './ChatWidget.module.scss'

const BASE_URL = 'https://sitka-agents-api-19652360144.us-central1.run.app'

const ChatWidget = () => {
  const [isOpen, setIsOpen] = useState(false)
  const [messages, setMessages] = useState([])
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState(null)
  const [inputMessage, setInputMessage] = useState('')
  const [isSending, setIsSending] = useState(false)
  const [conversationId, setConversationId] = useState(null)
  
  const messagesEndRef = useRef(null)

  // Load conversationId from localStorage on mount
  useEffect(() => {
    const savedId = localStorage.getItem('chatWidget_conversationId')
    if (savedId) {
      setConversationId(savedId)
    }
  }, [])

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }

  useEffect(() => {
    scrollToBottom()
  }, [messages])

  useEffect(() => {
    const fetchConversation = async () => {
      if (!isOpen || !conversationId) return
      
      setIsLoading(true)
      setError(null)
      
      try {
        // Simple GET request - no headers needed
        const response = await fetch(`${BASE_URL}/conversations/${conversationId}`)
        
        if (!response.ok) {
          throw new Error('Failed to fetch conversation')
        }
        
        const data = await response.json()
        
        if (data.success && data.conversation) {
          const filteredMessages = data.conversation.messages.filter(message => 
            (message.role === 'user' || message.role === 'assistant') && 
            message.content
          )
          setMessages(filteredMessages)
        }
      } catch (err) {
        setError(err.message)
        console.error('Error fetching conversation:', err)
      } finally {
        setIsLoading(false)
      }
    }
  
    fetchConversation()
  }, [isOpen, conversationId])

  const toggleChat = () => {
    setIsOpen(!isOpen)
  }

  const resetConversation = () => {
    localStorage.removeItem('chatWidget_conversationId')
    setConversationId(null)
    setMessages([])
    setError(null)
  }

  const renderMessage = (message, index) => {
    const isUser = message.role === 'user'
    const text = message.content[0].text

    return (
      <div 
        key={index} 
        className={`${classes.message} ${isUser ? classes.messageUser : classes.messageAssistant}`}
      >
        <div className={classes.messageContent}>
          <ReactMarkdown 
            components={{
              a: ({ href, children }) => (
                <a href={href} target="_blank" rel="noopener noreferrer">
                  {children}
                </a>
              )
            }}
          >
            {text}
          </ReactMarkdown>
        </div>
      </div>
    )
  }

  const handleSendMessage = async (e) => {
    e.preventDefault()
    if (inputMessage.trim() && !isSending) {
      const messageText = inputMessage.trim()
      setInputMessage('')
      setIsSending(true)
      
      // Create user message object with correct structure
      const userMessage = {
        role: 'user',
        content: [{ text: messageText }]
      }
      
      // Add user message to messages array immediately (optimistic update)
      setMessages(prevMessages => [...prevMessages, userMessage])
      
      try {
        // Send message to chat endpoint
        const response = await fetch(`${BASE_URL}/chat`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            conversationId: conversationId,
            message: messageText
          })
        })
        
        if (!response.ok) {
          throw new Error('Failed to send message')
        }
        
        const data = await response.json()
        
        if (data.success && data.response) {
          // If we got a conversationId from the response, store it
          if (data.conversationId && !conversationId) {
            setConversationId(data.conversationId)
            localStorage.setItem('chatWidget_conversationId', data.conversationId)
          }
          
          // Create assistant message object with correct structure
          const assistantMessage = {
            role: 'assistant',
            content: [{ text: data.response }]
          }
          
          // Add assistant response to messages array
          setMessages(prevMessages => [...prevMessages, assistantMessage])
        } else {
          throw new Error('Invalid response from server')
        }
        
      } catch (err) {
        console.error('Error sending message:', err)
        setError(err.message)
        
        // Remove the user message if sending failed
        setMessages(prevMessages => prevMessages.slice(0, -1))
      } finally {
        setIsSending(false)
      }
    }
  }

  return (
    <div className={classes.chatWidget}>
      {/* Chat Bubble */}
      <button 
        className={`${classes.chatBubble} ${isOpen ? classes.chatBubbleOpen : ''}`}
        onClick={toggleChat}
        aria-label="Toggle chat"
      >
        {isOpen ? '✕' : '💬'}
      </button>

      {/* Chat Window */}
      {isOpen && (
        <div className={classes.chatWindow}>
          <div className={classes.chatHeader}>
            <h3>Chat Support</h3>
            <div className={classes.chatHeaderButtons}>
              <button 
                className={classes.resetButton}
                onClick={resetConversation}
                aria-label="Reset conversation"
                title="Reset conversation"
              >
                🔄
              </button>
              <button 
                className={classes.closeButton}
                onClick={toggleChat}
                aria-label="Close chat"
              >
                ✕
              </button>
            </div>
          </div>
          
          <div className={classes.chatMessages}>
            {isLoading && (
              <div className={classes.loadingMessage}>
                Loading conversation...
              </div>
            )}
            
            {error && (
              <div className={classes.errorMessage}>
                Error: {error}
              </div>
            )}
            
            {!isLoading && !error && messages.length === 0 && (
              <div className={classes.emptyMessage}>
                No messages yet. Start a conversation!
              </div>
            )}
            
            {messages.map((message, index) => renderMessage(message, index))}
            
            {isSending && (
              <div className={classes.message}>
                <div className={classes.messageContent}>
                  <div className={classes.typingIndicator}>
                    Assistant is typing...
                  </div>
                </div>
              </div>
            )}
            
            <div ref={messagesEndRef} />
          </div>
          
          <div className={classes.chatInput}>
            <form onSubmit={handleSendMessage} className={classes.inputForm}>
              <input
                type="text"
                value={inputMessage}
                onChange={(e) => setInputMessage(e.target.value)}
                placeholder="Type your message..."
                className={classes.messageInput}
                disabled={isSending}
              />
              <button
                type="submit"
                className={classes.sendButton}
                disabled={!inputMessage.trim() || isSending}
              >
                {isSending ? 'Sending...' : 'Send'}
              </button>
            </form>
          </div>
        </div>
      )}
    </div>
  )
}

export default ChatWidget 